import type { PageType, PaymentType, SearchCategory, SearchType } from '../../enums';
import type { OfferCharges, PointsConversion } from '../hotelsUtils/types';
import { ComponentType } from '../../enums';
import { ActionType } from '../../enums/actionType';
import {
  calculateId,
  calculateTotalNights,
  calculateTotalPointsValueAnyPayWith,
  formatDate,
  getTotalPrice,
  isPromotionExclusive,
  isPropertyLuxury,
} from '../hotelsUtils/hotelsUtils';

export interface CreateViewItemListEventPayloadOptions {
  adults: number;
  availableProperties?: number | null;
  availableRooms?: number;
  category: SearchCategory;
  checkIn: string | Date;
  checkOut: string | Date;
  children: number;
  currency: string;
  infants: number;
  listName: string;
  location: string;
  payWith: PaymentType;
  pointsConversion: PointsConversion;
  quantity: number | null;
  results: {
    offer: {
      charges: OfferCharges;
      luxOffer?: boolean;
    };
    property: {
      category: string | null;
      ctaMessage?: string | null;
      ctaMessageCategory?: string | null;
      hasOffer: boolean;
      promotionName: string | null;
      id: string | null;
      name: string | null;
      hasDepositPayableOffer: boolean;
      recommend: boolean;
      rebook: boolean;
    };
    roomType: {
      name: string;
    };
  }[];
  type: SearchType;
  pageType: PageType;
}

export interface CreateViewItemListEventPayloadReturn {
  ecommerce: {
    currency: string;
    item_list_id: string;
    item_list_name: string;
    items: {
      item_id: string | null;
      item_name: string | null;
      item_variant: string;
      item_category: string | null;
      index: number;
      quantity: number;
      price: number;
      points_value: number;
      strikethrough_price: boolean;
      luxe: boolean;
      recommend: boolean;
      has_deposit_pay: boolean;
      exclusive_offer: boolean;
      rebook: boolean;
      has_offer: boolean;
      offer_type: string | null;
      cta_message_category?: string | null;
      cta_message?: string | null;
    }[];
  };
  event_data: {
    action: ActionType.VIEW;
    available_property_count: number | null;
    available_room_count: number | null;
    component_type: ComponentType.ITEM_LIST;
    page_type: PageType;
    end_date?: string;
    search_category: SearchCategory;
    search_payment_toggle: PaymentType;
    search_term: string;
    search_type: SearchType;
    start_date?: string;
    travellers_adult: number;
    travellers_children: number;
    travellers_infant: number;
  };
}

export const createViewItemListEventPayload = ({
  listName,
  location,
  currency,
  type,
  category,
  payWith,
  availableProperties,
  availableRooms,
  checkIn,
  checkOut,
  adults,
  children,
  infants,
  results,
  pointsConversion,
  pageType,
}: CreateViewItemListEventPayloadOptions): CreateViewItemListEventPayloadReturn => {
  const numberOfNights = calculateTotalNights(checkIn, checkOut);

  return {
    event_data: {
      action: ActionType.VIEW,
      component_type: ComponentType.ITEM_LIST,
      page_type: pageType,
      search_term: location,
      search_type: type,
      search_category: category,
      search_payment_toggle: payWith,
      available_property_count: availableProperties ?? null,
      available_room_count: availableRooms ?? null,
      ...(checkIn && {
        start_date: formatDate(checkIn),
      }),
      ...(checkOut && { end_date: formatDate(checkOut) }),
      travellers_adult: adults,
      travellers_children: children,
      travellers_infant: infants,
    },
    ecommerce: {
      currency,
      item_list_id: calculateId(listName),
      item_list_name: listName,
      items: results.map(({ property, roomType, offer }, index) => {
        const pointsValue = calculateTotalPointsValueAnyPayWith(offer.charges, pointsConversion);
        const cashValue = getTotalPrice(offer.charges);
        const hasStrikethroughPrice = Number(offer.charges.strikethrough?.price.amount) > 0;
        const isLuxOffer = offer?.luxOffer ? true : isPropertyLuxury(property.promotionName ?? undefined);
        return {
          item_id: property.id,
          item_name: property.name,
          item_variant: roomType.name,
          item_category: property.category,
          index,
          quantity: numberOfNights,
          price: cashValue,
          points_value: pointsValue,
          luxe: isLuxOffer,
          recommend: property.recommend,
          has_deposit_pay: property.hasDepositPayableOffer,
          exclusive_offer: isPromotionExclusive(property.promotionName ?? undefined),
          rebook: property.rebook,
          has_offer: property.hasOffer,
          offer_type: property.promotionName,
          ...(property.ctaMessage && { cta_message: property.ctaMessage }),
          ...(property.ctaMessageCategory && { cta_message_category: property.ctaMessageCategory }),
          strikethrough_price: hasStrikethroughPrice,
        };
      }),
    },
  };
};
